import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { CostPerRecipeCalculatorClient } from './ui/CostPerRecipeCalculatorClient';

interface CostPerRecipeCalculatorProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const CostPerRecipeCalculator: React.FC<CostPerRecipeCalculatorProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Free Recipe Cost Calculator"
            description="Calculate the exact cost of any recipe with our free tool. Break down ingredient costs to find your total recipe cost and cost per serving. Perfect for home bakers and small businesses."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="cost-per-recipe"
        >
            <CostPerRecipeCalculatorClient />
            <CostPerRecipeCalculatorContent />
        </ToolContainer>
    );
};

const CostPerRecipeCalculatorContent: React.FC = () => (
    <div className="mt-12 space-y-12 pt-8 border-t border-brand-border text-brand-text-body">
        {/* SEO Intro Section */}
        <div className="prose prose-pink max-w-none">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">Accurate Recipe Costing for Home Bakers</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Profitability starts with knowing your exact <strong>Cost of Goods Sold (COGS)</strong>. A common mistake for home bakery businesses is guessing ingredient costs. This free <strong>Recipe Cost Calculator</strong> helps you break down bulk ingredients (like a 50lb bag of flour) into the exact amount used in your cookie or cake recipe. It calculates the total batch cost and the cost per serving, so you know exactly how much to charge to make a profit.
                Tired of manual entry? <a href="/saas" className="text-brand-pink font-bold hover:underline">Track inventory and costs automatically with Bakery Pro</a>.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Price Your Baking
                </h3>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Calculate COGS:</strong> Find the raw ingredient cost.</li>
                    <li><strong>Add Labor:</strong> Factor in your time.</li>
                    <li><strong>Add Overhead:</strong> Utilities, packaging, etc.</li>
                    <li><strong>Add Profit:</strong> Aim for a 20-30% food cost percentage.</li>
                </ol>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Frequently Asked Questions</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            Should I include packaging costs?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Yes! Boxes, cake boards, and ribbon are part of the direct cost of the product.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
